import React, {
	createContext,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import type { Toast, ToastContextType } from '../types/toast';
import { createToast } from '../utils/toast-utils';

export const ToastContext = createContext<ToastContextType | undefined>(
	undefined
);

// Provider component
export function ToastProvider({ children }: { children: React.ReactNode }) {
	const [toasts, setToasts] = useState<Toast[]>([]);
	const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

	const removeToast = useCallback((id: string) => {
		setToasts((prev) => prev.filter((toast) => toast.id !== id));
		// Clear timer if exists
		const timer = timersRef.current.get(id);
		if (timer) {
			clearTimeout(timer);
			timersRef.current.delete(id);
		}
	}, []);

	const addToast = useCallback(
		(toast: Omit<Toast, 'id'>) => {
			const newToast = createToast({
				...toast,
				// Set default duration if not provided
				duration: toast.duration ?? (toast.type === 'error' ? 6000 : 4000),
			});

			setToasts((prev) => {
				// Limit max toasts to prevent memory issues and stacking
				const updated = [...prev, newToast];
				return updated.length > 3 ? updated.slice(-3) : updated;
			});

			// Auto-remove toast after duration
			if (newToast.duration && newToast.duration > 0) {
				const timer = setTimeout(
					() => removeToast(newToast.id),
					newToast.duration
				);
				timersRef.current.set(newToast.id, timer);
			}
		},
		[removeToast]
	);

	// Cleanup timers on unmount
	useEffect(() => {
		const timers = timersRef.current;
		return () => {
			timers.forEach((timer) => clearTimeout(timer));
			timers.clear();
		};
	}, []);

	const value = React.useMemo(
		() => ({
			toasts,
			addToast,
			removeToast,
		}),
		[toasts, addToast, removeToast]
	);

	return (
		<ToastContext.Provider value={value}>
			{children}
			<ToastContainer toasts={toasts} removeToast={removeToast} />
		</ToastContext.Provider>
	);
}

interface ToastContainerProps {
	toasts: Toast[];
	removeToast: (id: string) => void;
}

function ToastContainer({ toasts, removeToast }: ToastContainerProps) {
	if (toasts.length === 0) return null;

	return (
		<div className='fixed top-4 right-4 z-[9999] space-y-2 max-w-sm'>
			{toasts.map((toast) => (
				<ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
			))}
		</div>
	);
}

interface ToastItemProps {
	toast: Toast;
	onRemove: (id: string) => void;
	index: number;
}

function ToastItem({ toast, onRemove }: Omit<ToastItemProps, 'index'>) {
	const [isVisible, setIsVisible] = useState(false);

	// Animate in on mount
	useEffect(() => {
		const timer = setTimeout(() => setIsVisible(true), 10);
		return () => clearTimeout(timer);
	}, []);

	const handleRemove = () => {
		setIsVisible(false);
		// Delay actual removal to allow animation
		setTimeout(() => onRemove(toast.id), 300);
	};

	const getToastStyles = () => {
		const baseStyles = `
			flex items-center justify-between p-3 rounded-lg shadow-lg transition-all duration-300 ease-in-out transform
			${
				isVisible
					? 'translate-x-0 opacity-100 scale-100'
					: 'translate-x-full opacity-0 scale-95'
			}
			min-w-0 w-full backdrop-blur-sm
		`.trim();

		switch (toast.type) {
			case 'success':
				return `${baseStyles} bg-green-600 text-white border border-green-500`;
			case 'error':
				return `${baseStyles} bg-red-600 text-white border border-red-500`;
			case 'warning':
				return `${baseStyles} bg-yellow-600 text-white border border-yellow-500`;
			case 'info':
				return `${baseStyles} bg-blue-600 text-white border border-blue-500`;
			default:
				return `${baseStyles} bg-gray-600 text-white border border-gray-500`;
		}
	};

	const getIcon = () => {
		switch (toast.type) {
			case 'success':
				return (
					<svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
						<path
							fillRule='evenodd'
							d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
							clipRule='evenodd'
						/>
					</svg>
				);
			case 'error':
				return (
					<svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
						<path
							fillRule='evenodd'
							d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
							clipRule='evenodd'
						/>
					</svg>
				);
			case 'warning':
				return (
					<svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
						<path
							fillRule='evenodd'
							d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
							clipRule='evenodd'
						/>
					</svg>
				);
			case 'info':
				return (
					<svg className='w-5 h-5 mr-3' fill='currentColor' viewBox='0 0 20 20'>
						<path
							fillRule='evenodd'
							d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
							clipRule='evenodd'
						/>
					</svg>
				);
			default:
				return null;
		}
	};

	return (
		<div className={getToastStyles()}>
			<div className='flex items-center min-w-0 flex-1'>
				{getIcon()}
				<span className='text-sm font-medium truncate'>{toast.message}</span>
			</div>
			<button
				onClick={handleRemove}
				className='ml-3 flex-shrink-0 text-white hover:text-gray-200 transition-colors p-1 rounded-full hover:bg-black hover:bg-opacity-20'>
				<svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
					<path
						fillRule='evenodd'
						d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
						clipRule='evenodd'
					/>
				</svg>
			</button>
		</div>
	);
}
